import React, { useEffect, useState } from "react";
import baseURL from "../../authComponents/baseURL/BaseURL";
import { BiSearch } from "react-icons/bi";
import * as XLSX from "xlsx";

const Cities = () => {
  const [cities, setCities] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalEntries, setTotalEntries] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        setLoading(true);
        const response = await baseURL.get("/get_cities");

        let data = response.data;
        if (typeof data === "string") {
          try {
            data = JSON.parse(data);
          } catch (parseError) {
            console.error("Error parsing response data:", parseError);
          }
        }

        if (data && data.cities && Array.isArray(data.cities)) {
          setCities(data.cities);
          setFilteredCities(data.cities);
          setTotalEntries(data.cities.length);
        } else {
          console.error("Unexpected API response format:", response.data);
        }
      } catch (error) {
        console.error("Error fetching cities:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCities();
  }, []);

  useEffect(() => {
    if (searchTerm === "") {
      setFilteredCities(cities);
    } else {
      const filtered = cities.filter(
        (city) =>
          (city.name &&
            city.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (city.code &&
            city.code.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (city.iana_timezone &&
            city.iana_timezone.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredCities(filtered);
    }
    setCurrentPage(1);
  }, [searchTerm, cities]);

  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredCities.slice(
    indexOfFirstEntry,
    indexOfLastEntry
  );

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Calculate total pages
  const totalPages = Math.ceil(filteredCities.length / entriesPerPage);

  // Function to export data to Excel
  const exportToExcel = () => {
    setIsExporting(true);
    try {
      // Prepare the data for export
      const exportData = filteredCities.map((city, index) => ({
        ID: city.id || "-",
        Name: city.name || "-",
        Code: city.code || "-",
        "IANA Timezone": city.iana_timezone || "-",
      }));

      // Create a worksheet
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      const columnWidths = [{ wch: 5 }, { wch: 20 }, { wch: 10 }, { wch: 25 }];
      worksheet["!cols"] = columnWidths;

      // Create a new workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Cities");

      const date = new Date();
      const fileName = `Cities_${date.getFullYear()}-${(date.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}_${date
        .getHours()
        .toString()
        .padStart(2, "0")}-${date
        .getMinutes()
        .toString()
        .padStart(2, "0")}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error("Failed to export to Excel:", error);
      alert("Failed to export data. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-semibold text-gray-700 font-serif">
          Cities
        </h1>
        <button
          onClick={exportToExcel}
          disabled={isExporting || filteredCities.length === 0}
          className={`px-4 py-2 rounded-md shadow-sm ${
            isExporting || filteredCities.length === 0
              ? "bg-gray-300 cursor-not-allowed"
              : "bg-green-600 text-white hover:bg-green-700"
          } transition-colors duration-300 flex items-center`}
        >
          {isExporting ? (
            "Downloading..."
          ) : (
            <>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Download Cities Data
            </>
          )}
        </button>
      </div>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
        <div className="flex items-center mb-4 md:mb-0">
          <span className="mr-2">Show</span>
          <select
            className="border rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-[#730000]"
            value={entriesPerPage}
            onChange={handleEntriesChange}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className="ml-2">entries</span>
        </div>

        <div className="relative">
          <span className="mr-2">Search:</span>
          <input
            type="text"
            className="border rounded px-3 py-1 w-64 focus:outline-none focus:ring-2 focus:ring-[#730000]"
            placeholder="Search cities..."
            value={searchTerm}
            onChange={handleSearch}
          />
          <BiSearch className="absolute right-3 top-2 text-gray-400" />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#730000] mx-auto"></div>
          <p className="mt-2">Loading...</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                    NAME
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                    CODE
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                    IANA_TIMEZONE
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {currentEntries.map((city, index) => (
                  <tr key={city.id || index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {city.id || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {city.name || ""}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {city.code || ""}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {city.iana_timezone || ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center mt-4">
            <p className="text-sm text-gray-600">
              Showing {filteredCities.length > 0 ? indexOfFirstEntry + 1 : 0} to{" "}
              {Math.min(indexOfLastEntry, filteredCities.length)} of{" "}
              {filteredCities.length} entries
            </p>

            <div className="flex">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded-l hover:bg-gray-100 disabled:opacity-50"
              >
                Previous
              </button>

              {totalPages <= 7 ? (
                [...Array(totalPages)].map((_, index) => (
                  <button
                    key={index}
                    onClick={() => paginate(index + 1)}
                    className={`px-3 py-1 border-t border-b border-r ${
                      currentPage === index + 1
                        ? "bg-[#730000] text-white"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    {index + 1}
                  </button>
                ))
              ) : (
                <>
                  <button
                    onClick={() => paginate(1)}
                    className={`px-3 py-1 border-t border-b border-r ${
                      currentPage === 1
                        ? "bg-[#730000] text-white"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    1
                  </button>

                  {currentPage > 3 && (
                    <button className="px-3 py-1 border-t border-b border-r">
                      ...
                    </button>
                  )}

                  {currentPage !== 1 && currentPage !== totalPages && (
                    <button className="px-3 py-1 border-t border-b border-r bg-[#730000] text-white">
                      {currentPage}
                    </button>
                  )}

                  {currentPage < totalPages - 2 && (
                    <button className="px-3 py-1 border-t border-b border-r">
                      ...
                    </button>
                  )}

                  {totalPages > 1 && (
                    <button
                      onClick={() => paginate(totalPages)}
                      className={`px-3 py-1 border-t border-b border-r ${
                        currentPage === totalPages
                          ? "bg-[#730000] text-white"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      {totalPages}
                    </button>
                  )}
                </>
              )}

              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages || totalPages === 0}
                className="px-3 py-1 border-t border-b border-r rounded-r hover:bg-gray-100 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Cities;
